import React, { createContext, useState, useContext, useEffect, useCallback } from 'react'; 
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth, getUserInfo } from '../services/userService'; 
// Potentially import backend check functions later if needed

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true); 
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [userInfoLoading, setUserInfoLoading] = useState(false);
  const [justCompletedSignup, setJustCompletedSignup] = useState(false); 

  // Function to fetch user info from backend
  const fetchUserInfo = useCallback(async (user) => {
    // console.log('AuthContext: fetchUserInfo called with user:', user ? user.uid : 'no user');
    
    if (!user) {
      // console.log('AuthContext: fetchUserInfo - No user provided, returning early');
      return;
    }
    
    try {
      // console.log('AuthContext: fetchUserInfo - Setting loading to true');
      setUserInfoLoading(true);
      
      // console.log('AuthContext: fetchUserInfo - Getting ID token...');
      const token = await user.getIdToken();
      // console.log('AuthContext: fetchUserInfo - Got token, length:', token ? token.length : 'no token');
      
      // console.log('AuthContext: fetchUserInfo - Calling getUserInfo service...');
      const userData = await getUserInfo(token);
      // console.log('AuthContext: fetchUserInfo - Got userData:', userData);
      
      setUserInfo(userData);
      // console.log('AuthContext: User info fetched and set successfully:', userData);
    } catch (error) {
      // console.error('AuthContext: Failed to fetch user info:', error);
      // console.error('AuthContext: Error details:', error.message, error.stack);
      setUserInfo(null);
    } finally {
      // console.log('AuthContext: fetchUserInfo - Setting loading to false');
      setUserInfoLoading(false);
    }
  }, []);

  // Function to call AFTER successful backend verification (via checkAuthenticationStatus) OR on initial auth state detection
  const appLogin = useCallback((user = null) => { 
    // console.log('AuthContext: appLogin called, setting isAuthenticated = true');
    setIsAuthenticated(true);
    // Fetch user info after successful login
    if (user) {
      fetchUserInfo(user);
    }
  }, [fetchUserInfo]);

  // Function to handle application logout
  const appLogout = useCallback(async () => { 
    // console.log('AuthContext: appLogout called');
    try {
      await signOut(auth); // Sign out from Firebase
      setIsAuthenticated(false); // Update app state
      setFirebaseUser(null);
      setUserInfo(null); // Clear user info
    } catch (error) {
      // console.error("AuthContext: Error during sign out:", error);
      setIsAuthenticated(false); 
      setFirebaseUser(null);
      setUserInfo(null);
    }
  }, []);

  useEffect(() => {
    // Listener for Firebase auth state changes
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      // console.log('AuthContext: Firebase user state changed:', user ? user.uid : 'null');
      setFirebaseUser(user); // Update the firebaseUser state
      
      // If Firebase user exists (logged in or session persisted), log the app in.
      if (user) {
        appLogin(user); // Set isAuthenticated to true and fetch user info
      } else {
        // If Firebase user logs out (or no session), ensure app state is logged out.
        // Note: appLogout already handles this, but added for clarity in the listener's logic.
        // Consider if appLogout should be the ONLY place setting isAuthenticated to false.
        setIsAuthenticated(false);
        setUserInfo(null); // Clear user info on logout
      }
      
      // Initial loading is done once we get the first user status
      setIsLoading(false); 
    });

    return () => unsubscribe(); 
  }, [appLogin]); // Add appLogin to dependency array

  // Define the checkAuthenticationStatus function
  // This function is now explicitly called by UI components AFTER successful backend interaction
  const checkAuthenticationStatus = useCallback(async (userCredential = null, loginUserData = null, isFromSignup = false) => {
    // console.log('AuthContext: checkAuthenticationStatus called explicitly by UI with credential:', userCredential ? userCredential.user.uid : 'null');
    const user = userCredential ? userCredential.user : null; 

    if (user) {
      // Since this is called after successful backend login/signup, we can now set the app as authenticated.
      // console.log('AuthContext: Backend verification succeeded (implied), proceeding to appLogin.');
      
      // Set the signup flag if this is from a signup process
      if (isFromSignup) {
        setJustCompletedSignup(true);
      }
      
      // If we have loginUserData from the login endpoint, use it as fallback user info
      if (loginUserData) {
        // console.log('AuthContext: Using loginUserData as initial user info');
        setUserInfo(loginUserData);
        setUserInfoLoading(false);
      }
      
      appLogin(user); // Pass user to appLogin to fetch user info
    } else {
      // This case might occur if called after a failed attempt, ensure logged out state.
      // console.log('AuthContext: No user provided to checkAuthenticationStatus, ensuring logged out state.');
      setIsAuthenticated(false);
      setUserInfo(null);
    }
  }, [appLogin]); // Dependency array includes appLogin

  // Function to clear the signup flag (called after welcome modal is shown)
  const clearSignupFlag = useCallback(() => {
    setJustCompletedSignup(false);
  }, []);

  // Add checkAuthenticationStatus to the value object
  return (
    <AuthContext.Provider value={{ 
        isAuthenticated, 
        isLoading, 
        firebaseUser, 
        userInfo,
        userInfoLoading,
        justCompletedSignup,
        appLogin, 
        appLogout, 
        checkAuthenticationStatus,
        fetchUserInfo,
        clearSignupFlag
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
